import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Gift, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function PromoCodeInput({ onSuccess }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRedeem = async () => {
    if (!code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/promo-codes/redeem`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ code: code.trim() })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to redeem promo code');
      }

      setSuccess(true);
      toast.success(data.message || 'Promo code redeemed successfully!');
      
      // Reload the page to refresh premium status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error redeeming promo code:', error);
      toast.error(error.message || 'Invalid or expired promo code');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card style={{ background: 'rgba(16, 185, 129, 0.1)', border: '2px solid var(--primary-green)' }}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8" style={{ color: 'var(--primary-green)' }} />
            <div>
              <h3 className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                Promo Code Redeemed!
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                You now have full lifetime access to CalmGuard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ background: 'var(--bg-card)', border: '1px solid var(--primary-blue)' }}>
      <CardContent className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <Gift className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: 'var(--primary-blue)' }} />
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1" style={{ color: 'var(--text-primary)' }}>
              Have a Promo Code?
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Redeem your code to get free lifetime access to all features.
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder="Enter promo code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && handleRedeem()}
            disabled={loading}
            className="flex-1"
            style={{ 
              borderColor: 'var(--primary-blue)',
              color: 'var(--text-primary)'
            }}
          />
          <Button
            onClick={handleRedeem}
            disabled={loading || !code.trim()}
            style={{ 
              background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)',
              color: 'white'
            }}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redeeming...
              </>
            ) : (
              'Redeem'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
